import type { DropdownFilter } from "./Dropdown/Dropdown";
import type { HeaderCategory } from "./HeaderCategory.d.ts";
import type { Suggestion } from "./SearchCombobox/SearchCombobox";

const ROOT_CATEGORY_NAME = "Alle Kategorien";
const ROOT_CATEGORY_ID = 0;

type Category = { id: string; localizedNames: { [key: string]: string } };

function buildResultObject(
  originalQuery: string,
  completion = "",
  categoryId = ROOT_CATEGORY_ID,
  catName = "",
  objectId = "",
): Suggestion {
  // Use root category as fallback
  const categoryName = categoryId === 0 ? ROOT_CATEGORY_NAME : catName;
  const completionShort = completion.substring(originalQuery.length);

  return {
    originalQuery,
    selectedId: categoryId,
    text: completionShort,
    categoryName,
    objectId,
  };
}

function buildResultWithCategoryName(
  category: Category,
  completion: string,
  query: string,
  hit: AlgoliaHit,
  currentCategoryId?: number,
): Suggestion {
  const categoryName =
    currentCategoryId !== parseInt(category.id, 10)
      ? category.localizedNames["de_DE"]
      : "";

  return buildResultObject(
    query,
    completion,
    parseInt(category.id, 10),
    categoryName,
    hit.objectID,
  );
}

export type AlgoliaHit = {
  objectID: string;
  categories: {
    id: string;
    canonicalName: null;
    localizedNames: { [key: string]: string };
    depth: number;
    pathToRoot: {
      id: string;
      localizedNames: {
        de_DE: string;
      };
    }[];
  }[];
  completion: string;
  wordLength: number;
  conversions: number;
  frequency: number;
  conversionsWeight: number;
  frequencyWeight: number;
  _highlightResult: {
    completion: {
      value: string;
      matchLevel: string;
      fullyHighlighted: boolean;
      matchedWords: string[];
    };
  };
};

function formatFirstHit(
  hit: AlgoliaHit,
  query: string,
  currentCategoryId?: number,
): Suggestion[] {
  const { categories = [], completion = "" } = hit;

  function compareByCategoryId(hit1: Suggestion, hit2: Suggestion): number {
    // if it's the current categoryId the element should be on top
    return hit1.selectedId === currentCategoryId
      ? -1
      : hit1.selectedId - hit2.selectedId;
  }

  return categories
    .map((category: Category) =>
      buildResultWithCategoryName(
        category,
        completion,
        query,
        hit as AlgoliaHit,
        currentCategoryId,
      ),
    )
    .sort(compareByCategoryId);
}

function formatSubsequentHits(
  hits: AlgoliaHit[],
  query: string,
  currentCategoryId?: number,
): Suggestion[] {
  return hits.map((hit) => {
    const { categories } = hit;
    const hitCategory = categories.find(
      (category) => parseInt(category.id, 10) === currentCategoryId,
    );

    return buildResultObject(
      query,
      hit.completion,
      hitCategory && currentCategoryId,
      undefined,
      hit.objectID,
    );
  });
}

export default function transformAlgoliaToSuggestionResults(
  searchResponse: { hits: AlgoliaHit[]; query: string },
  currentCategoryId?: number,
): Suggestion[] {
  const { hits = [], query = "" } = searchResponse;
  return [
    ...formatFirstHit(hits[0], query, currentCategoryId),
    ...formatSubsequentHits(hits.slice(1), query, currentCategoryId),
  ];
}

type LocationSuggestions = { [key: string]: string };

export function transformLocationToSuggestionResults(
  searchResponse: LocationSuggestions,
): Suggestion[] {
  return Object.entries(searchResponse).map(([key, value]) => {
    const categoryId = parseInt(key.slice(1), 10);
    return {
      text: value,
      selectedId: categoryId,
      objectId: key,
    };
  });
}

export function transformCategoriesToFilter(
  categories: HeaderCategory[],
): DropdownFilter[] {
  if (!categories) {
    return [];
  }
  return categories.map((category) => ({
    value: category.id,
    name: category.categoryName,
    children: transformCategoriesToFilter(category.children),
    selected: category.selected,
  }));
}
